import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Send, X, Bot, Loader2, ThumbsUp, ThumbsDown, Plus, Trash2 } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { streamAiChat, fetchConversation, fetchConversations, deleteConversation, submitFeedback } from "../../api/ai.js";
import MonitorProposalCard from "./MonitorProposalCard.jsx";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ToolBadge({ name }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-700">
      <Loader2 className="h-3 w-3 animate-spin" />
      {name.replace(/_/g, " ")}
    </span>
  );
}

function FeedbackButtons({ messageId, initialFeedback, conversationId, onFeedback }) {
  const { t } = useTranslation("ai_assistant");
  const [feedback, setFeedback] = useState(initialFeedback || null);
  const [loading, setLoading] = useState(false);

  async function submit(value) {
    if (loading || !conversationId || !messageId) return;
    setLoading(true);
    try {
      await submitFeedback(conversationId, messageId, value);
      setFeedback(value);
      onFeedback?.(messageId, value);
    } catch {
      toast.error(t("feedback.error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-1.5 flex gap-1">
      <button
        onClick={() => submit("positive")}
        disabled={loading || feedback !== null}
        className={`rounded p-0.5 transition-colors ${
          feedback === "positive"
            ? "text-teal-600"
            : "text-gray-300 hover:text-teal-500 disabled:opacity-40"
        }`}
        title={t("feedback.helpful")}
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => submit("negative")}
        disabled={loading || feedback !== null}
        className={`rounded p-0.5 transition-colors ${
          feedback === "negative"
            ? "text-red-500"
            : "text-gray-300 hover:text-red-400 disabled:opacity-40"
        }`}
        title={t("feedback.not_helpful")}
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function Message({ msg, conversationId, onDismissMonitor }) {
  const { t } = useTranslation("ai_assistant");
  const isUser = msg.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${isUser ? "" : "w-full"}`}>
        {isUser ? (
          <div className="rounded-2xl rounded-br-sm bg-teal-600 px-3 py-2 text-sm text-white">
            {msg.content}
          </div>
        ) : (
          <div className="rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-700">
            {msg.toolsActive && msg.toolsActive.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {msg.toolsActive.map((tool) => (
                  <ToolBadge key={tool} name={tool} />
                ))}
              </div>
            )}
            {msg.content && (
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="mb-1 ml-4 list-disc space-y-0.5">{children}</ul>,
                  ol: ({ children }) => <ol className="mb-1 ml-4 list-decimal space-y-0.5">{children}</ol>,
                  li: ({ children }) => <li>{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  code: ({ inline, children }) =>
                    inline ? (
                      <code className="rounded bg-gray-200 px-1 py-0.5 font-mono text-xs">{children}</code>
                    ) : (
                      <pre className="mt-1 overflow-x-auto rounded bg-gray-200 p-2 font-mono text-xs">
                        <code>{children}</code>
                      </pre>
                    ),
                  a: ({ href, children }) => (
                    <a href={href} className="text-teal-600 underline" target="_blank" rel="noreferrer">
                      {children}
                    </a>
                  ),
                  table: ({ children }) => (
                    <div className="my-1 overflow-x-auto">
                      <table className="w-full border-collapse text-xs">{children}</table>
                    </div>
                  ),
                  thead: ({ children }) => <thead className="bg-gray-200">{children}</thead>,
                  th: ({ children }) => (
                    <th className="border border-gray-300 px-2 py-1 text-left font-semibold">{children}</th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-gray-300 px-2 py-1">{children}</td>
                  ),
                  h1: ({ children }) => <p className="font-semibold">{children}</p>,
                  h2: ({ children }) => <p className="font-semibold">{children}</p>,
                  h3: ({ children }) => <p className="font-medium">{children}</p>,
                }}
              >
                {msg.content}
              </Markdown>
            )}
            {msg.proposals && msg.proposals.length > 0 && (
              <div>
                {msg.proposals.map((m) => (
                  <MonitorProposalCard
                    key={m.id}
                    monitor={m}
                    onDismiss={onDismissMonitor}
                  />
                ))}
              </div>
            )}
            {!msg.content && !msg.toolsActive?.length && !msg.proposals?.length && (
              <span className="text-gray-400 italic">{t("thinking")}</span>
            )}
            {/* Feedback for persisted assistant messages only */}
            {msg.id && !msg.id.startsWith("local-") && (
              <FeedbackButtons
                messageId={msg.id}
                initialFeedback={msg.feedback}
                conversationId={conversationId}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Welcome message (not persisted)
// ---------------------------------------------------------------------------

function makeWelcome(t) {
  return {
    id: "local-welcome",
    role: "assistant",
    content: t("welcome"),
    toolsActive: [],
    proposals: [],
    feedback: null,
  };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AIAssistant({ onClose }) {
  const { t } = useTranslation("ai_assistant");
  const [messages, setMessages] = useState(() => [makeWelcome(t)]);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const cancelRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Load most recent conversation on mount
  useEffect(() => {
    (async () => {
      try {
        const convs = await fetchConversations({ limit: 1 });
        if (convs && convs.length > 0) {
          const latest = await fetchConversation(convs[0].id);
          if (latest && latest.messages && latest.messages.length > 0) {
            const hydrated = latest.messages.map((m) => ({
              ...m,
              toolsActive: [],
              proposals: [],
            }));
            setMessages(hydrated);
            setConversationId(latest.id);
          }
        }
      } catch {
        // No history or network error — start fresh
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!loadingHistory) inputRef.current?.focus();
  }, [loadingHistory]);

  function historyForApi() {
    return messages
      .filter((m) => !m.id?.startsWith("local-") && (m.role === "user" || m.role === "assistant"))
      .map((m) => ({ role: m.role, content: m.content || "" }));
  }

  function startNewConversation() {
    setMessages([makeWelcome(t)]);
    setConversationId(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  async function handleDeleteConversation() {
    if (!conversationId) return;
    try {
      await deleteConversation(conversationId);
      startNewConversation();
      toast.success(t("success_delete"));
    } catch {
      toast.error(t("error_delete"));
    }
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || streaming) return;

    const userMsgId = `local-user-${Date.now()}`;
    const asstId = `local-asst-${Date.now()}`;

    const userMsg = { id: userMsgId, role: "user", content: text, toolsActive: [], proposals: [], feedback: null };
    const asstMsg = { id: asstId, role: "assistant", content: "", toolsActive: [], proposals: [], feedback: null };

    setMessages((prev) => [...prev, userMsg, asstMsg]);
    setInput("");
    setStreaming(true);

    const history = [...historyForApi(), { role: "user", content: text }];

    cancelRef.current = streamAiChat(
      history,
      conversationId,
      (event) => {
        switch (event.type) {
          case "tool_start":
            setMessages((prev) =>
              prev.map((m) =>
                m.id === asstId
                  ? { ...m, toolsActive: [...(m.toolsActive || []), event.name] }
                  : m
              )
            );
            break;

          case "tool_end":
            setMessages((prev) =>
              prev.map((m) =>
                m.id === asstId
                  ? { ...m, toolsActive: (m.toolsActive || []).filter((n) => n !== event.name) }
                  : m
              )
            );
            break;

          case "monitors_created":
            setMessages((prev) =>
              prev.map((m) =>
                m.id === asstId
                  ? { ...m, proposals: [...(m.proposals || []), ...event.monitors] }
                  : m
              )
            );
            break;

          case "text":
            setMessages((prev) =>
              prev.map((m) =>
                m.id === asstId ? { ...m, content: event.content } : m
              )
            );
            break;

          case "error":
            setMessages((prev) =>
              prev.map((m) =>
                m.id === asstId ? { ...m, content: `⚠️ ${event.message}` } : m
              )
            );
            setStreaming(false);
            break;

          case "done":
            // Replace local placeholder IDs with the real conversation_id
            if (event.conversation_id) {
              setConversationId(event.conversation_id);
            }
            setStreaming(false);
            break;
        }
      },
      (err) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === asstId ? { ...m, content: `⚠️ ${err}` } : m
          )
        );
        setStreaming(false);
      }
    );
  }

  function handleDismissMonitor(monitorId) {
    setMessages((prev) =>
      prev.map((m) => ({
        ...m,
        proposals: (m.proposals || []).filter((p) => p.id !== monitorId),
      }))
    );
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-teal-600" />
          <span className="text-sm font-semibold text-gray-800">{t("title")}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={startNewConversation}
            disabled={streaming}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
            title={t("new_conversation")}
          >
            <Plus className="h-4 w-4" />
          </button>
          {conversationId && (
            <button
              onClick={handleDeleteConversation}
              disabled={streaming}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500 disabled:opacity-40"
              title={t("delete_conversation")}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t("close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Message list */}
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {loadingHistory ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : (
          messages.map((msg) => (
            <Message
              key={msg.id}
              msg={msg}
              conversationId={conversationId}
              onDismissMonitor={handleDismissMonitor}
            />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 px-4 py-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("placeholder")}
            rows={1}
            disabled={streaming || loadingHistory}
            className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:bg-gray-50"
            style={{ maxHeight: "120px" }}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
          />
          {streaming ? (
            <button
              onClick={() => { cancelRef.current?.(); setStreaming(false); }}
              className="flex-shrink-0 rounded-lg bg-red-100 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-200"
            >
              {t("stop")}
            </button>
          ) : (
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loadingHistory}
              className="flex-shrink-0 rounded-lg bg-teal-600 p-2 text-white hover:bg-teal-700 disabled:opacity-40"
              aria-label={t("send_hint")}
            >
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="mt-1.5 text-center text-xs text-gray-400">
          {t("send_hint")}
        </p>
      </div>
    </div>
  );
}
